// Distance between two song keys, measured in semitones between their roots.
//
// The scale-array lookups in autoTranspose can't answer this. calculateSemitones finds the first
// key in MAJOR_SCALE_KEYS or MINOR_SCALE_KEYS and then looks for the second one in whichever
// array matched, so a major-to-minor change never resolves and comes back as 0 — the same silent
// zero it returns for single-notation accidentals like 'A#'. Both are artefacts of matching key
// names against a table rather than reading the pitch out of them.
//
// Parsing the root instead handles every spelling the app produces — 'C', 'Cm', 'C#/Db',
// 'C#m/Dbm', 'A#', 'Bb', 'Bbm' — and treats mode as what it is here: not part of the distance.
// Moving a song from C to Dm moves its tonic up two semitones, so the MIDI moves up two with it.

const NATURAL_PITCH_CLASSES = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

// Leading note letter plus an optional accidental. Anything after it — a minor 'm', the second
// half of a combined spelling — carries no pitch of its own.
const ROOT_PATTERN = /^([A-Ga-g])([#b]?)/;

const SEMITONES_PER_OCTAVE = 12;

/**
 * The pitch class of a key's root, 0 (C) to 11 (B).
 *
 * @param {string} key - A song key in any spelling the app stores or displays
 * @returns {number|null} null when the key can't be read
 */
export function getKeyPitchClass(key) {
  if (!key || typeof key !== 'string') return null;

  // 'C#m/Dbm' carries both spellings of one pitch, so either half answers the question
  const [spelling] = key.trim().split('/');
  const match = ROOT_PATTERN.exec(spelling.trim());

  if (!match) return null;

  const [, letter, accidental] = match;
  let pitchClass = NATURAL_PITCH_CLASSES[letter.toUpperCase()];

  if (accidental === '#') pitchClass += 1;
  else if (accidental === 'b') pitchClass -= 1;

  return ((pitchClass % SEMITONES_PER_OCTAVE) + SEMITONES_PER_OCTAVE) % SEMITONES_PER_OCTAVE;
}

/**
 * How far to move to get from one key's root to another's, taking the short way round the circle
 * so a clip stays in its original register instead of leaping an octave.
 *
 * A tritone is the one move with no short way — six semitones up and six down land on the same
 * pitch — so the direction there is a convention, resolved upwards. calculateSemitones happens to
 * resolve it downwards for half its pairs and upwards for the other half, purely because it reads
 * the sign off the two keys' positions in an array that starts at C; there is no musical fact
 * underneath that to preserve.
 *
 * @param {string} fromKey - The key being moved from
 * @param {string} toKey - The key being moved to
 * @returns {number} Semitones, -5 to +6; 0 when either key can't be read
 */
export function getKeyRootDistance(fromKey, toKey) {
  const from = getKeyPitchClass(fromKey);
  const to = getKeyPitchClass(toKey);

  if (from === null || to === null) return 0;

  const distance = ((to - from) % SEMITONES_PER_OCTAVE + SEMITONES_PER_OCTAVE) % SEMITONES_PER_OCTAVE;

  return distance > 6 ? distance - SEMITONES_PER_OCTAVE : distance;
}
